import React from 'react';
import { Autocomplete, Box, TextField, Typography, createFilterOptions } from '@mui/material';
import type { Employee } from '../../../store/slices/employeeSlice';
import { sbg } from '../../../theme/sbgTokens';

/**
 * Inline table fields stay quiet until hovered or focused, so a page of rows
 * reads as values rather than a wall of form fields. Apply to an OutlinedInput
 * root (a Select, or an Autocomplete's input root).
 */
export const quietFieldSx = {
  bgcolor: 'transparent',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: sbg.border.hover },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: sbg.focus.ring },
  '&.Mui-disabled': { bgcolor: 'transparent' },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
} as const;

/** Two-line option: the value on top, a quiet explanation under it. */
export const OptionText: React.FC<{ primary: React.ReactNode; secondary?: React.ReactNode }> = ({ primary, secondary }) => (
  <Box sx={{ display: 'grid', minWidth: 0 }}>
    <Typography variant="body1" component="span" noWrap>
      {primary}
    </Typography>
    {secondary && (
      <Typography variant="caption" component="span" noWrap sx={{ color: sbg.text.secondary }}>
        {secondary}
      </Typography>
    )}
  </Box>
);

export interface EmployeeOption {
  id: string;
  name: string;
  email: string;
  /** `none` = the "Not linked" choice; `orphan` = a saved link whose employee isn't in the loaded list. */
  kind: 'none' | 'employee' | 'orphan';
}

export const NOT_LINKED: EmployeeOption = { id: '', name: 'Not linked', email: 'No employee record', kind: 'none' };

/** "Not linked" first, then every employee. Build once per employee list and share across rows. */
export const toEmployeeOptions = (employees: Employee[]): EmployeeOption[] => [
  NOT_LINKED,
  ...employees.map((employee): EmployeeOption => ({ id: employee._id, name: employee.name, email: employee.email, kind: 'employee' })),
];

// Matches on name and email; an orphaned saved link is only ever the current value, never a choice.
const matchNameOrEmail = createFilterOptions<EmployeeOption>({ stringify: (option) => `${option.name} ${option.email}` });
const filterOptions: typeof matchNameOrEmail = (options, state) =>
  matchNameOrEmail(options.filter((option) => option.kind !== 'orphan'), state);

/**
 * The linked-employee cell (book §11.4: over seven options, a searchable
 * combobox). Quiet until hovered or focused; type a name or email to narrow it.
 */
const LinkedEmployeePicker: React.FC<{
  /** Who this row is, for the field's accessible name. */
  personName: string;
  linkedId: string;
  /** Saved employee name, used when the linked employee isn't in the loaded list. */
  fallbackName?: string | null;
  options: EmployeeOption[];
  optionById: Map<string, EmployeeOption>;
  disabled?: boolean;
  onChange: (employeeId: string) => void;
}> = ({ personName, linkedId, fallbackName, options, optionById, disabled, onChange }) => {
  const known = linkedId ? optionById.get(linkedId) : NOT_LINKED;
  const orphan: EmployeeOption | null = known
    ? null
    : { id: linkedId, name: fallbackName || 'Linked employee', email: '', kind: 'orphan' };
  const value = known ?? (orphan as EmployeeOption);
  const rowOptions = orphan ? [...options, orphan] : options;
  const unlinked = value.kind === 'none';

  return (
    <Autocomplete
      size="small"
      disableClearable
      autoHighlight
      blurOnSelect
      options={rowOptions}
      value={value}
      disabled={disabled}
      filterOptions={filterOptions}
      getOptionLabel={(option) => option.name}
      getOptionKey={(option) => option.kind === 'none' ? '__not-linked' : option.id}
      isOptionEqualToValue={(option, current) => option.id === current.id}
      onChange={(_, option) => {
        if (option.id !== linkedId) onChange(option.id);
      }}
      renderOption={({ key, ...props }, option) => (
        <li key={key} {...props}>
          <OptionText primary={option.name} secondary={option.email || undefined} />
        </li>
      )}
      noOptionsText="No employees match"
      slotProps={{ popper: { placement: 'bottom-start', sx: { minWidth: 320 } } }}
      sx={{ ml: '-12px', width: 252, maxWidth: 'calc(100% + 12px)' }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputProps={{ ...params.inputProps, 'aria-label': `Linked employee for ${personName}`, spellCheck: false }}
          sx={{
            '& .MuiOutlinedInput-root': quietFieldSx,
            ...(unlinked ? { '& .MuiAutocomplete-input': { color: sbg.text.secondary } } : null),
          }}
        />
      )}
    />
  );
};

export default LinkedEmployeePicker;
